<template>
  <fieldset
    class="quantityPickerWrapper"
    :class="{
      isFirstGlove: props.gloveNumber === 1
    }"
  >
    <hr />
    <div is="legend" class="text-bold">Weitere Handschuhgröße</div>
    <label :for="`qb_${calcedDisposableGlove.sku}`">
      <b class="text-blue">
        {{ calcedDisposableGlove.mengePackung }} {{ calcedDisposableGlove.einheit }}
      </b>
      <span
        v-if="
          calcedDisposableGlove.preisStueckBrutto || calcedDisposableGlove.preisStueckBrutto === 0
        "
        class="text-xs"
      >
        {{ toCurrency(calcedDisposableGlove.preisStueckBrutto) }}
      </span>
    </label>
    <PflegeQuantitybutton
      :id="`qb_${calcedDisposableGlove.sku}`"
      v-model.number="calcedDisposableGlove.gewaehlteAnzahl"
      :min="0"
      :max="calcedDisposableGlove.hoechstanzahl"
      :data-sku="calcedDisposableGlove.sku"
      :disabled="allergie.length >= 3"
      readonly
    />
    <div
      v-if="calcedDisposableGlove.gewaehlteAnzahl === calcedDisposableGlove.hoechstanzahl"
      class="text-s"
    >
      Maximale Menge erreicht
    </div>
    <PflegeSelect
      v-model="size"
      label="Größe wählen"
      :options="[
        { label: 'S', value: 'S' },
        { label: 'M', value: 'M' },
        { label: 'L', value: 'L' },
        { label: 'XL', value: 'XL' }
      ]"
    />
  </fieldset>
  <div :class="{ isFirstGlove: props.gloveNumber === 1 }">
    <fieldset class="allergy-toggle">
      <legend class="text-bold text-blue">
        Bestehen Allergien gegen Latex, Vinyl oder Nitril?
      </legend>
      <PflegeRadio
        v-model="hasAllergie"
        :options="[
          { label: 'Nein, keine', value: false },
          { label: 'Ja, es liegen Allergien vor', value: true }
        ]"
        variant="normal"
        flow="column"
      />
    </fieldset>
    <fieldset v-if="hasAllergie" class="allergy-selection">
      <legend>und zwar gegen</legend>
      <PflegeCheckbox v-model="allergie" label="Latex" value="Latex" />
      <PflegeCheckbox v-model="allergie" label="Vinyl" value="Vinyl" />
      <PflegeCheckbox v-model="allergie" label="Nitril" value="Nitril" />
    </fieldset>
    <p v-if="allergie.length >= 3" class="allergy-warning">
      <PflegeIcon name="warning-fill" color="warning" />
      Wenn Sie gegen alle drei Materialien allergisch sind, können wir Ihnen keine Einmalhandschuhe
      anbieten.
    </p>
    <button class="blank remove-btn" @click="onDelete()">
      <PflegeIcon name="remove-line" />
      Produkt entfernen
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toCurrency } from '../../utils/formatter'

/* remove attr-transfer, cause of multiple child-nodes */
defineOptions({ inheritAttrs: false })

const product = defineModel({ type: Object, required: true })
const props = defineProps({
  defaultDisposableGloveSku: {
    type: String,
    required: false,
    default: ''
  },
  gloveNumber: {
    type: Number,
    required: false,
    default: 0
  },
  preselectedDisposableGloveSku: {
    type: String,
    required: false,
    default: null
  }
})

/* calculate correct glove variance */
const hasAllergie = ref(false)
const allergie = ref([])
const size = ref('M')
let lastSelectedDisposableGloveSku = null
const calcedDisposableGlove = computed(() => {
  let calcedMaterial = 'Vinyl'
  if (allergie.value.includes('Vinyl')) {
    calcedMaterial = 'Latex'
  }
  if (allergie.value.includes('Vinyl') && allergie.value.includes('Latex')) {
    calcedMaterial = 'Nitril'
  }
  const disposableGlove =
    product.value.varianten.find(
      (p) =>
        p.eigenschaften?.material?.value === calcedMaterial &&
        p.eigenschaften?.groesse?.value === size.value
    ) || product.value.varianten.find((p) => p.sku === props.defaultDisposableGloveSku)

  if (lastSelectedDisposableGloveSku && lastSelectedDisposableGloveSku !== disposableGlove.sku) {
    const lastDisposableGlove = product.value.varianten.find(
      (p) => p.sku === lastSelectedDisposableGloveSku
    )
    disposableGlove.gewaehlteAnzahl = lastDisposableGlove.gewaehlteAnzahl
    lastDisposableGlove.gewaehlteAnzahl = 0
  }
  if (allergie.value.length >= 3) {
    disposableGlove.gewaehlteAnzahl = 0
  }
  lastSelectedDisposableGloveSku = disposableGlove?.sku ?? null
  disposableGlove.allergies = allergie
  return disposableGlove
})

/* pre-select inputs on preselected glove characteristics */
if (props.preselectedDisposableGloveSku || props.defaultDisposableGloveSku) {
  const preselectedDisposableGlove = product.value.varianten.find(
    (p) => p.sku === props.preselectedDisposableGloveSku || props.defaultDisposableGloveSku
  )
  if (preselectedDisposableGlove) {
    size.value = preselectedDisposableGlove.eigenschaften.groesse.value
    if (preselectedDisposableGlove.gewaehlteAllergien) {
      hasAllergie.value = preselectedDisposableGlove.gewaehlteAllergien.length > 0
      allergie.value = preselectedDisposableGlove.gewaehlteAllergien
    } else {
      switch (preselectedDisposableGlove.eigenschaften.material.value) {
        case 'Nitril':
          hasAllergie.value = true
          allergie.value = ['Vinyl', 'Latex']
          break
        case 'Latex':
          hasAllergie.value = true
          allergie.value = ['Vinyl']
          break
        default:
          hasAllergie.value = false
          allergie.value = []
          break
      }
    }
  }
}
/* remove glove */
const emit = defineEmits(['delete'])
const onDelete = () => {
  calcedDisposableGlove.value.gewaehlteAnzahl = 0
  emit('delete')
}
</script>

<style scoped>
.curapick .curapick-tile .isFirstGlove .remove-btn,
.curapick .curapick-tile .isFirstGlove.quantityPickerWrapper > div[is='legend'],
.curapick .curapick-tile .isFirstGlove hr {
  display: none;
}
.curapick .curapick-tile .allergy-selection > * {
  padding-inline: 1rem;
}
.curapick .curapick-tile .allergy-selection legend {
  padding-block: 0.75rem;
  color: var(--blue);
  font-weight: 700;
}
.curapick .curapick-tile .allergy-selection > .checkbox:not(:last-child) {
  margin-block-end: 1rem;
}
.curapick .curapick-tile .allergy-toggle .radio {
  padding-block-start: 0.5rem;
  padding-inline: 1rem;
}
.curapick .curapick-tile .allergy-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}
.curapick .curapick-tile .allergy-warning svg {
  flex-shrink: 0;
  margin-block-start: 0.25rem;
}
.curapick .curapick-tile .quantityPickerWrapper hr {
  border-block-start: 1px solid var(--mint);
  color: var(--mint);
  margin: 0;
}
.curapick .curapick-tile .quantityPickerWrapper > div[is='legend'] {
  padding-block: 0.5rem;
}
.curapick .curapick-tile .quantityPickerWrapper:not(.isFirstGlove) > *:not(hr) {
  padding-inline-start: 1rem;
}
.curapick .curapick-tile .remove-btn {
  padding: 1.25rem 1.1rem 0;
  font-size: 0.9em;
  gap: 0.75rem;
}
.curapick .curapick-tile .remove-btn svg {
  color: var(--blue);
  background-color: var(--white);
  border-radius: 50%;
  height: 1.1rem;
  width: 1.1rem;
  padding: 0.2rem;
  border: 1px solid var(--blue);
}
</style>
